/**
 * IdleLogout — 60-minute idle-out for every authenticated user.
 *
 * V2.0.36 (Feb 22, 2026) — first drop.
 *
 * How it works
 * ============
 *  - Once the user is authenticated (AuthContext has a `user`), we start
 *    a rolling `IDLE_MS` timer.
 *  - Any of the whitelisted activity events resets the timer.
 *  - When the timer has `WARN_MS` remaining, we surface a countdown modal
 *    with two buttons — "Keep me signed in" (resets) and "Sign out now".
 *  - When the timer hits zero we hit `POST /auth/logout`, clear the auth
 *    context, and route to `/login?reason=idle` (or the candidate variant).
 *  - Cross-tab sync via a `naukri:idle-activity` localStorage key so that
 *    activity in tab A also resets the timer in tab B.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// 60 minutes idle, 60 seconds warning window.
const IDLE_MS = 60 * 60 * 1000;
const WARN_MS = 60 * 1000;
const CROSS_TAB_KEY = "naukri:idle-activity";
// Debounce activity events to avoid resetting on every mousemove pixel.
const ACTIVITY_DEBOUNCE_MS = 500;

const ACTIVITY_EVENTS = [
    "mousedown", "keydown", "touchstart", "wheel", "scroll", "click", "pointerdown",
];

export default function IdleLogout() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const [showWarning, setShowWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARN_MS / 1000));
    // Refs so effect stays stable and doesn't restart on every render.
    const lastActivityRef = useRef(Date.now());
    const timerRef = useRef(null);
    const countdownRef = useRef(null);

    const forceLogout = useCallback(async () => {
        // Best-effort server call; even if it fails we still clear locally.
        try { await api.post("/auth/logout"); } catch (_) { /* offline */ }
        try { setUser(null); } catch (_) { /* noop */ }
        setShowWarning(false);
        const target = (window.location.pathname || "").startsWith("/candidate")
            ? "/candidate/login?reason=idle"
            : "/login?reason=idle";
        // Full reload guarantees every in-memory state (SSE, chat sockets,
        // service-worker registrations tied to the user) is torn down.
        window.location.replace(target);
    }, [setUser]);

    const scheduleTimers = useCallback(() => {
        // Clear anything previously scheduled.
        if (timerRef.current) clearTimeout(timerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        setShowWarning(false);
        // Schedule the warning modal WARN_MS before the actual logout.
        timerRef.current = setTimeout(() => {
            setSecondsLeft(Math.floor(WARN_MS / 1000));
            setShowWarning(true);
            countdownRef.current = setInterval(() => {
                setSecondsLeft((s) => {
                    if (s <= 1) {
                        clearInterval(countdownRef.current);
                        forceLogout();
                        return 0;
                    }
                    return s - 1;
                });
            }, 1000);
        }, IDLE_MS - WARN_MS);
    }, [forceLogout]);

    const registerActivity = useCallback(() => {
        const now = Date.now();
        if (now - lastActivityRef.current < ACTIVITY_DEBOUNCE_MS) return;
        lastActivityRef.current = now;
        try { localStorage.setItem(CROSS_TAB_KEY, String(now)); } catch (_) { /* noop */ }
        scheduleTimers();
    }, [scheduleTimers]);

    useEffect(() => {
        // Only run for signed-in users. Anonymous public pages don't need this.
        if (!user || !user.id) return;
        scheduleTimers();

        // Same-tab activity
        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, registerActivity, { passive: true }));
        // Cross-tab activity via storage event
        const onStorage = (e) => {
            if (e.key === CROSS_TAB_KEY) {
                lastActivityRef.current = Date.now();
                scheduleTimers();
            }
        };
        window.addEventListener("storage", onStorage);

        return () => {
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, registerActivity));
            window.removeEventListener("storage", onStorage);
            if (timerRef.current) clearTimeout(timerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [user, registerActivity, scheduleTimers]);

    // Also react to the axios interceptor firing a "session ended" event
    // (see lib/api.js). This happens when the user has been kicked because
    // another device signed in with the same account.
    useEffect(() => {
        const onKick = (e) => {
            const reason = e?.detail?.reason || "new_device";
            toast.error("Session ended", {
                description:
                    reason === "new_device"
                        ? "You signed in on another device. Redirecting you to log in again."
                        : "Your session has ended. Please log in again.",
                duration: 4000,
            });
            setTimeout(() => {
                try { setUser(null); } catch (_) { /* noop */ }
                const target = (window.location.pathname || "").startsWith("/candidate")
                    ? `/candidate/login?reason=${reason}`
                    : `/login?reason=${reason}`;
                window.location.replace(target);
            }, 1500);
        };
        window.addEventListener("naukri:session-ended", onKick);
        return () => window.removeEventListener("naukri:session-ended", onKick);
    }, [setUser]);

    if (!user || !user.id || !showWarning) return null;

    return (
        <div
            data-testid="idle-logout-warning"
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
        >
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
                <div className="p-5">
                    <h3 className="text-lg font-black text-ink tracking-tight">Still there?</h3>
                    <p className="text-sm text-ink/70 mt-1.5">
                        For your security, we'll sign you out after 60 minutes of inactivity.
                    </p>
                    <div className="mt-4 flex items-center justify-center py-4">
                        <div className="text-4xl font-black text-cobalt tabular-nums" data-testid="idle-countdown">
                            {String(Math.floor(secondsLeft / 60)).padStart(2, "0")}
                            <span className="text-ink/40">:</span>
                            {String(secondsLeft % 60).padStart(2, "0")}
                        </div>
                    </div>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <button
                            type="button"
                            onClick={registerActivity}
                            data-testid="idle-keep-signed-in"
                            className="flex-1 h-10 px-4 rounded-full bg-ink text-white text-xs font-black uppercase tracking-wider hover:opacity-90"
                        >
                            Keep me signed in
                        </button>
                        <button
                            type="button"
                            onClick={forceLogout}
                            data-testid="idle-signout-now"
                            className="flex-1 h-10 px-4 rounded-full border border-ink/15 text-ink text-xs font-black uppercase tracking-wider hover:bg-black/5"
                        >
                            Sign out now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
